import { getOwner, setOwner } from '@ember/application';
import VisualWorkflowBuilderV2 from 'cio/components/visual-workflow-builder-v2';
import { action } from '@ember/object';
import { registerDestructor } from '@ember/destroyable';
import { VWBComponentWrapper } from 'cio/utils/visual-workflow-builder/context';
import { MouseMode } from 'cio/utils/visual-workflow-builder/types';

export function createInertiaManagerForComponent(vwbComponent: VisualWorkflowBuilderV2) {
  let wrapper = new VWBComponentWrapper(vwbComponent);
  let owner = getOwner(vwbComponent);
  return new InertiaManager(wrapper, owner);
}

export interface InertiaManagerParams {
  vwbComponent: VisualWorkflowBuilderV2;

  canvas: HTMLDivElement | null;
}

export class InertiaManager {
  params: InertiaManagerParams;

  constructor(params: InertiaManagerParams, owner: any) {
    this.params = params;

    setOwner(this, owner);
  }

  // The params could be configurable, but are hardcoded for now.
  // The values below use seconds, but the calculations further below use ms for more precision
  //
  // The "friction" of the decay effect
  // 1/s
  #resistance = 8;

  // The minimum speed of the mouse pointer to trigger the inertial decay
  // px/s
  #minSpeed = 200;

  // The speed at which the decay effect should end
  // This is necessary because an exponential decay never reaches 0, so we need to define a cutoff
  // px/s
  #endSpeed = 50;

  // Converts the params from seconds to milliseconds
  get lambda() {
    return this.#resistance / 1000;
  }
  get vMin() {
    return this.#minSpeed / 1000;
  }
  get vF() {
    return this.#endSpeed / 1000;
  }

  @action
  registerCanvasEvents() {
    this.params.canvas?.addEventListener('vwb:canvas:mousedown', this.#onMouseDown);

    registerDestructor(this.params.vwbComponent, () => {
      this.params.canvas?.removeEventListener('vwb:canvas:mousedown', this.#onMouseDown);
      document.removeEventListener('mousemove', this.#onMouseMove);
      this.params.canvas?.removeEventListener('vwb:canvas:mouseup', this.#onMouseUp);
    });
  }

  mousePositions: { x: number; y: number; timestamp: number }[] = [];

  #onMouseDown = (event: CustomEvent) => {
    cancelAnimationFrame(this.#frame);

    if (event.detail.mouseMode !== MouseMode.Scroll) {
      return;
    }

    // Register a mousemove on the entire document so we can track
    // the mouse regardless of where it moves
    document.addEventListener('mousemove', this.#onMouseMove);
    this.params.canvas?.addEventListener('vwb:canvas:mouseup', this.#onMouseUp);
  };

  #onMouseMove = (event: MouseEvent) => {
    if (!this.params.vwbComponent.isMouseDown) {
      return;
    }

    let { mousePositions } = this;

    cancelAnimationFrame(this.#frame);

    let currentPosition = { x: event.clientX, y: event.clientY, timestamp: Date.now() };

    // Record the last 4 mouse positions, we'll use them later to calculate
    // average velocities.
    // We use an average because using a single data point from mouse movements
    // is too noisy and can lead to erratic behavior.
    this.mousePositions = [...mousePositions, currentPosition].slice(-4);
  };

  currentOffset = { x: 0, y: 0 };
  // Start timestamp
  decayStartTime = 0;
  // Total duration of decaying slowdown, in ms
  decayDuration = 0;

  // The starting speed of the mouse pointer, when the mouse is released
  startSpeed = 0;
  // The angle of the velocity vector when the mouse is released
  angle = 0;

  #onMouseUp = (event: CustomEvent) => {
    document.removeEventListener('mousemove', this.#onMouseMove);
    this.params.canvas?.removeEventListener('vwb:canvas:mouseup', this.#onMouseUp);

    if ((event.detail.hasMovedMouse as boolean) && event.detail.mouseMode === MouseMode.Scroll) {
      let { lambda, vMin, vF, mousePositions } = this;

      if (mousePositions.length < 2) {
        return;
      }

      // Calculate average velocities from the mouse positions
      let velocitiesCount = mousePositions.length - 1;
      let { vxSum, vySum } = mousePositions.reduce(
        (sums, position, i, arr) => {
          if (i === 0) {
            return sums;
          }

          let previousPosition = arr[i - 1];
          let dt = position.timestamp - previousPosition.timestamp;
          let dx = position.x - previousPosition.x;
          let dy = position.y - previousPosition.y;
          let vx = dx / dt;
          let vy = dy / dt;

          return {
            vxSum: sums.vxSum + vx,
            vySum: sums.vySum + vy,
          };
        },
        { vxSum: 0, vySum: 0 },
      );
      let vx = vxSum / velocitiesCount;
      let vy = vySum / velocitiesCount;

      let speed = Math.hypot(vx, vy);

      let currentTime = Date.now();
      this.decayStartTime = currentTime;

      let lastMousePosition = mousePositions[mousePositions.length - 1];
      // Only trigger if the mouseup was released shortly after a mousemove,
      // otherwise the speed calculations are not reliable
      let isMouseUpRecent = currentTime - (lastMousePosition?.timestamp ?? currentTime) < 50;
      // Only trigger if the mouse speed exceeds the minimum
      let isFastEnough = speed > vMin;
      // Only trigger if the mouse speed is higher than the
      // configured end speed cutoff
      let isFasterThanEndSpeed = speed > vF;

      if (isMouseUpRecent && isFastEnough && isFasterThanEndSpeed) {
        // In the decay calculations we pretend that the mouse pointer
        // keeps moving in the direction it was when it was released,
        // with its speed decaying over time.
        // To make calculations easier, we assume we start at a 0,0
        // origin, and calculate the hypothetical mouse travel from there.
        this.currentOffset = {
          x: 0,
          y: 0,
        };
        // https://en.wikipedia.org/wiki/Exponential_decay
        // The time it takes for the velocity to decay to vF
        this.decayDuration = -Math.log(vF / speed) / lambda;

        // Save the angle of the velocity vector so we can calculate the X and Y offsets at each tick
        this.angle = Math.atan2(vy, vx);
        this.startSpeed = speed;

        this.mousePositions = [];
        requestAnimationFrame(() => this.#inertiaTick());
      }
    }
  };

  #frame = 0;

  #inertiaTick = () => {
    let { decayStartTime, decayDuration } = this;
    let dt = Date.now() - decayStartTime;

    // If the time since the mouseup is less than the calculated duration
    // of the decay, scroll the canvas. Otherwise the decay is over and we don't do anything.
    if (dt >= decayDuration) {
      return;
    }

    let { lambda, currentOffset, startSpeed } = this;

    // https://en.wikipedia.org/wiki/Exponential_decay
    // Current progress on the decay curve
    let distance = (startSpeed * (1 - Math.exp(-lambda * dt))) / lambda;

    let distanceX = Math.cos(this.angle) * distance;
    let distanceY = Math.sin(this.angle) * distance;

    // The distance the mouse would've traveled in the time since the mouseup
    // if it kept moving, with its speed slowing.
    let newOffset = {
      x: distanceX,
      y: distanceY,
    };

    // Round the values, because scroll positions are in whole pixels
    let delta = {
      x: Math.round(newOffset.x - currentOffset.x),
      y: Math.round(newOffset.y - currentOffset.y),
    };

    // Save the new offset so we can calculate the delta again in the next
    // tick
    this.currentOffset.x += delta.x;
    this.currentOffset.y += delta.y;

    // Scroll the canvas by the difference from the last tick.
    // We use a negative value because the page scrolls in the opposite
    // direction of the mouse pointer movement.
    this.params.vwbComponent.canvas!.scrollBy({ top: -delta.y, left: -delta.x, behavior: 'instant' });

    // Schedule the next scroll position update frame
    this.#frame = requestAnimationFrame(() => this.#inertiaTick());
  };
}
